/**
 * The duels on the games page (ADR 0076): each duel game the tenant offers, with what
 * waits on this player and their rating, and a way into its lobby.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getDuel } from "@/duels/registry";
import { useTenant } from "@/core/tenant/TenantProvider";
import { useDuelHub, type DuelGame, type Pace } from "./api";

export function DuelsSection() {
  const { t } = useTranslation();
  const tenant = useTenant();
  const offered = tenant.enabled_games.filter((key) => getDuel(key));
  const hub = useDuelHub(undefined, { enabled: offered.length > 0 });
  if (offered.length === 0 || !hub.data) return null;
  const games = hub.data.games.filter((game) => getDuel(game.key));
  if (games.length === 0) return null;
  return (
    <section aria-labelledby="games-duels" className="bt-games__section">
      <div className="bt-games__section-head">
        <h2 id="games-duels" className="bt-games__section-title">
          {t("duels.title")}
        </h2>
        <p className="bt-games__section-hint">{t("duels.hint")}</p>
      </div>
      <ul className="bt-game-index">
        {games.map((game) => (
          <DuelGameRow
            key={game.key}
            game={game}
            invitations={
              hub.data.running.filter(
                (d) =>
                  d.game_key === game.key &&
                  d.status === "invited" &&
                  !d.is_challenger &&
                  d.invitee !== null,
              ).length
            }
          />
        ))}
      </ul>
    </section>
  );
}

const PACE_ORDER: Pace[] = ["blitz", "rapid", "correspondence", "realtime"];

function DuelGameRow({ game, invitations }: { game: DuelGame; invitations: number }) {
  const { t } = useTranslation();
  const ns = getDuel(game.key)!.namespace;
  const best = PACE_ORDER.find((pace) => game.my_ratings[pace] !== undefined);
  return (
    <li className="bt-game-row">
      <div className="bt-game-row__name">
        <span className="bt-game-row__title">{t("name", { ns })}</span>
        <span className="bt-game-row__tagline">{t("tagline", { ns })}</span>
      </div>
      <p className="bt-game-row__mine">
        {game.your_turn > 0 || invitations > 0 ? (
          <span className="bt-game-row__waiting">
            {[
              game.your_turn > 0 ? t("duels.your_turn_count", { count: game.your_turn }) : null,
              invitations > 0 ? t("duels.invitations_count", { count: invitations }) : null,
            ]
              .filter(Boolean)
              .join(", ")}
          </span>
        ) : (
          <span className="bt-game-row__quiet">{t("duels.nothing_waiting")}</span>
        )}
      </p>
      <p className="bt-game-row__best">
        {best ? (
          <>
            {t("duels.your_rating", { pace: t(`duels.pace.${best}`) })}{" "}
            <span className="bt-game-row__figure">{game.my_ratings[best]}</span>
          </>
        ) : (
          <span className="bt-game-row__quiet">{t("duels.unrated_yet")}</span>
        )}
      </p>
      <div className="bt-game-row__actions">
        <Link to={`/duels/${game.key}`} className="bt-button bt-button--sm bt-button--primary">
          {game.your_turn > 0 || invitations > 0 ? t("duels.open_games") : t("duels.challenge")}
        </Link>
      </div>
    </li>
  );
}
